import ExcelJS from "exceljs";

const thinBorder = {
  top: { style: "thin", color: { argb: "FFCCCCCC" } },
  left: { style: "thin", color: { argb: "FFCCCCCC" } },
  bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
  right: { style: "thin", color: { argb: "FFCCCCCC" } },
};

/**
 * Thêm worksheet ma trận ngày (dùng chung cho Xuất Excel filter + tab xác nhận giờ công).
 * @returns {import("exceljs").Worksheet|null}
 */
function addStyledMatrixSheet(wb, { sheetName, aoa, title }) {
  if (!aoa?.length) return null;
  const numCols = aoa[0].length;
  const ws = wb.addWorksheet(sheetName.slice(0, 31), {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  ws.mergeCells(1, 1, 1, numCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getRow(1).height = title && String(title).includes("\n") ? 52 : 30;

  const headerRow = ws.getRow(2);
  aoa[0].forEach((val, idx) => {
    const c = headerRow.getCell(idx + 1);
    c.value = val;
    c.font = { bold: true, size: 11 };
    c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    c.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" },
    };
    c.border = thinBorder;
  });
  headerRow.height = 24;

  for (let r = 1; r < aoa.length; r += 1) {
    const row = ws.getRow(r + 2);
    row.height = 20;
    aoa[r].forEach((val, colIndex) => {
      const c = row.getCell(colIndex + 1);
      c.value = val;
      c.border = thinBorder;
      if (colIndex === 0) {
        c.alignment = { horizontal: "center", vertical: "middle" };
      } else if (colIndex >= 1 && colIndex <= 3) {
        c.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
      } else if (colIndex === numCols - 1) {
        c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      } else {
        c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      }
    });
  }

  for (let i = 1; i <= numCols; i += 1) {
    const col = ws.getColumn(i);
    if (i === 1) col.width = 6;
    else if (i === 2) col.width = 24;
    else if (i === 3) col.width = 22;
    else if (i === 4) col.width = 26;
    else if (i < numCols) col.width = 8;
    else col.width = 20;
  }

  return ws;
}

/**
 * @param {any[][]} aoa — hàng 0 = header, còn lại = dữ liệu
 * @param {{ branchLabel: string, year: number, month: number, mode: string, filename: string }} opts
 */
export async function downloadAttendanceMatrixExcel(aoa, opts) {
  const { branchLabel, year, month, mode, filename } = opts;
  if (!aoa?.length) return;

  const monthPadded = String(month).padStart(2, "0");
  const branchText = branchLabel?.trim() || "Tất cả chi nhánh";
  const title = `Dữ liệu chấm công tháng ${monthPadded} năm ${year} của ${branchText}`;

  const wb = new ExcelJS.Workbook();
  const sheetName =
    mode === "detail"
      ? "ChiTietTheoNgay"
      : mode === "actual_hours"
        ? "GioCongThucTe"
        : "NgayCong";
  addStyledMatrixSheet(wb, { sheetName, aoa, title });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Kết xuất tab xác nhận giờ công: sheet 1 = giờ thực tế theo ca (giống filter),
 * sheet 2 = bảng giải trình + tiêu đề kỳ bảng công (cho kế toán).
 *
 * @param {{ pivotAoA: any[][], year: number, month: number, branchLabel: string, filename: string,
 *   period?: { year_month?: string, status?: string, closed_at_fmt?: string, confirmation_requested_at_fmt?: string } | null,
 *   explanations?: { work_date?: string, employee_name?: string, branch_name?: string, reason?: string }[]
 * }} opts
 */
export async function downloadPayrollConfirmationExcel(opts) {
  const { pivotAoA, year, month, branchLabel, filename, period, explanations } = opts;
  if (!pivotAoA?.length) return;

  const monthPadded = String(month).padStart(2, "0");
  const branchText = branchLabel?.trim() || "Tất cả chi nhánh";

  let titleSheet1 = `Giờ công thực tế theo ca (xuất cho kế toán) — tháng ${monthPadded} năm ${year}`;
  titleSheet1 += `\n${branchText}`;
  if (period?.year_month) {
    titleSheet1 += `\nKỳ bảng công ${period.year_month}: ${
      period.status === "closed" ? "đã đóng" : "đang mở"
    }`;
    const bits = [];
    if (period.confirmation_requested_at_fmt) bits.push(`Yêu cầu xác nhận: ${period.confirmation_requested_at_fmt}`);
    if (period.closed_at_fmt) bits.push(`Đóng kỳ: ${period.closed_at_fmt}`);
    if (bits.length) titleSheet1 += `\n${bits.join(" | ")}`;
  }

  const wb = new ExcelJS.Workbook();

  addStyledMatrixSheet(wb, {
    sheetName: "GioCongThucTe",
    aoa: pivotAoA,
    title: titleSheet1,
  });

  const ws2 = wb.addWorksheet("Giai_trinh_NV", {
    views: [{ state: "frozen", ySplit: 2 }],
  });
  const ncol = 5;
  ws2.mergeCells(1, 1, 1, ncol);
  let t2val = `Giải trình giờ công của nhân viên — tháng ${monthPadded} năm ${year}\n${branchText}`;
  if (!period?.year_month) {
    t2val += "\n(Lưu ý: Chưa có bản ghi kỳ bảng công trong hệ thống cho tháng này — chỉ có dữ liệu chấm công)";
  }
  const t2 = ws2.getCell(1, 1);
  t2.value = t2val;
  t2.font = { bold: true, size: 13 };
  t2.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws2.getRow(1).height = 44;

  const hdrLabels = ["Stt", "Ngày làm việc", "Nhân viên", "Chi nhánh", "Nội dung giải trình"];
  const h2 = ws2.getRow(2);
  hdrLabels.forEach((label, idx) => {
    const c = h2.getCell(idx + 1);
    c.value = label;
    c.font = { bold: true, size: 11 };
    c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    c.border = thinBorder;
  });
  h2.height = 24;

  const rows = Array.isArray(explanations) ? explanations : [];
  if (rows.length === 0) {
    const r = ws2.getRow(3);
    r.height = 22;
    ws2.mergeCells(3, 1, 3, ncol);
    const c = r.getCell(1);
    c.value = "(Không có giải trình nào được lưu trong kỳ cho phạm vi / chi nhánh đã xuất.)";
    c.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    c.border = thinBorder;
  } else {
    rows.forEach((row, i) => {
      const r = ws2.getRow(3 + i);
      r.height = 22;
      const cells = [
        i + 1,
        row.work_date || "",
        row.employee_name || "",
        row.branch_name || "",
        row.reason || "",
      ];
      cells.forEach((val, colIndex) => {
        const c = r.getCell(colIndex + 1);
        c.value = val;
        c.border = thinBorder;
        if (colIndex === 0) c.alignment = { horizontal: "center", vertical: "middle" };
        else if (colIndex === 4) c.alignment = { horizontal: "left", vertical: "top", wrapText: true };
        else c.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
      });
    });
  }

  ws2.getColumn(1).width = 6;
  ws2.getColumn(2).width = 14;
  ws2.getColumn(3).width = 28;
  ws2.getColumn(4).width = 22;
  ws2.getColumn(5).width = 48;

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
